import React from 'react';
import _ from 'lodash';
import {
  diffItem,
  createChangeObj,
  calculateItemActualCostTotal,
  calculateItemCategorizedCostTotal,
} from 'common/requestDetails/utils';
import {produce} from 'immer';
import {
  VALUE_CHANGE_DISPLAY,
  CHANGES_TYPES,
  ADDITIONAL_INFORMATION,
  COST,
  ACTUAL_TOTAL,
  ACTUAL_ALLOWED_TOTAL,
  SCOPE_OF_WORK_TOTAL,
  ALLOWED_EXCESS_TOTAL,
  DISALLOWED_TOTAL,
  CATEGORIZED_SUM,
  NOT_FULLY_IN_SCOPE,
  CARD_TYPES,
} from 'common/requestDetails/constants';
import pluralize from 'pluralize';
import SimpleShared from '../shared/SimpleShared';
import {safeAdd, safeDivide, safeSubstract} from 'common/utils';
export default class SimpleHiring extends React.Component {
  constructor(props) {
    super(props);
    const notFullyInScope = _.get(
      props,
      'item.' + ADDITIONAL_INFORMATION + '.' + NOT_FULLY_IN_SCOPE,
      false
    );
    this.state = {
      lineItem: this.props.item,
      editMode: notFullyInScope,
    };
  }

  toggleEditMode = () => this.setState({editMode: !this.state.editMode});

  handleChange = (lineItem) => {
    return new Promise((resolve) => {
      this.setState({lineItem}, () => resolve());
    });
  };

  splitTotal = (totalAmount, size) => {
    if (totalAmount && _.isNumber(_.toNumber(totalAmount))) {
      let average = _.toString(_.toNumber(safeDivide(totalAmount, size).toFixed(2)));
      let array = new Array(size).fill(average);
      let newTotalAmount = safeAdd(...array);
      if (totalAmount !== newTotalAmount) {
        let lastPeriodValue = array[size - 1];
        let increment = safeSubstract(totalAmount - newTotalAmount);
        array[size - 1] = _.toString(safeAdd(lastPeriodValue, increment));
      }
      return array;
    }
    return new Array(size).fill(0);
  };

  resetBudgetCategorization = (item, notFullyInScope = false) => {
    this.toggleEditMode();

    const isStaffingBundled = this.props.costType == CARD_TYPES.STAFFING_BUNDLED;

    let actualTotalArr = _.get(
      item,
      ADDITIONAL_INFORMATION + '.' + ACTUAL_TOTAL,
      undefined
    );

    let defaultAllowedTotalArr = _.clone(actualTotalArr);

    if (isStaffingBundled) {
      _.set(item, ADDITIONAL_INFORMATION + '.isStaffingBundled', isStaffingBundled);
    }

    let actualAllowedTotalArr = _.get(
      item,
      ADDITIONAL_INFORMATION + '.' + ACTUAL_ALLOWED_TOTAL,
      undefined
    );
    _.set(item, ADDITIONAL_INFORMATION + '.' + NOT_FULLY_IN_SCOPE, notFullyInScope);

    if (!notFullyInScope || !actualAllowedTotalArr) {
      let itemActualTotal = calculateItemActualCostTotal(item);
      let zeroArray = undefined;
      if (defaultAllowedTotalArr) {
        zeroArray = new Array(defaultAllowedTotalArr.length).fill('0');
      }
      _.set(
        item,
        ADDITIONAL_INFORMATION + '.' + ACTUAL_ALLOWED_TOTAL + 'Sum',
        itemActualTotal
      );
      _.set(
        item,
        ADDITIONAL_INFORMATION + '.' + ACTUAL_ALLOWED_TOTAL,
        _.clone(defaultAllowedTotalArr)
      );

      _.set(item, ADDITIONAL_INFORMATION + '.' + DISALLOWED_TOTAL + 'Sum', 0);
      _.set(item, ADDITIONAL_INFORMATION + '.' + DISALLOWED_TOTAL, _.clone(zeroArray));

      _.set(item, ADDITIONAL_INFORMATION + '.' + ALLOWED_EXCESS_TOTAL + 'Sum', 0);
      _.set(
        item,
        ADDITIONAL_INFORMATION + '.' + ALLOWED_EXCESS_TOTAL,
        _.clone(zeroArray)
      );

      _.set(
        item,
        ADDITIONAL_INFORMATION + '.' + SCOPE_OF_WORK_TOTAL + 'Sum',
        itemActualTotal
      );
      _.set(
        item,
        ADDITIONAL_INFORMATION + '.' + SCOPE_OF_WORK_TOTAL,
        _.clone(defaultAllowedTotalArr)
      );

      _.set(
        item,
        ADDITIONAL_INFORMATION + '.' + CATEGORIZED_SUM + 'Sum',
        itemActualTotal
      );
      _.set(
        item,
        ADDITIONAL_INFORMATION + '.' + CATEGORIZED_SUM,
        _.clone(defaultAllowedTotalArr)
      );
    } else
      this.handleTotalCostChange(
        ADDITIONAL_INFORMATION + '.' + ACTUAL_TOTAL,
        defaultAllowedTotalArr
      );
    return item;
  };

  handleBlur = (fieldName) => {
    if (this.props.editable) {
      let {lineItem} = this.state;
      let {item} = this.props;
      lineItem = _.cloneDeep(lineItem);
      let itemToDisplay = {
        ...lineItem,
        [ADDITIONAL_INFORMATION]: {
          ..._.get(item, ADDITIONAL_INFORMATION), // need to bring in additional information saved outside this component
          ..._.get(lineItem, ADDITIONAL_INFORMATION),
        },
      };

      this.setState((prevState) => ({
        lineItem: {
          ...prevState.lineItem,
          changes: itemToDisplay.changes,
          bocTotals: itemToDisplay.bocTotals,
        },
      }));
      this.props.saveItem(this.props.sourcePath, itemToDisplay, fieldName);
    }
  };

  handleItemChange = (name, value, ind = -1) => {
    if (
      name.startsWith(ADDITIONAL_INFORMATION + '.' + SCOPE_OF_WORK_TOTAL + 'Sum') ||
      name.startsWith(ADDITIONAL_INFORMATION + '.' + ALLOWED_EXCESS_TOTAL + 'Sum') ||
      name.startsWith(ADDITIONAL_INFORMATION + '.' + DISALLOWED_TOTAL + 'Sum')
    ) {
      return this.handleTotalSumCostChange(name, value);
    } else if (
      name.startsWith(ADDITIONAL_INFORMATION + '.' + SCOPE_OF_WORK_TOTAL) ||
      name.startsWith(ADDITIONAL_INFORMATION + '.' + ALLOWED_EXCESS_TOTAL) ||
      name.startsWith(ADDITIONAL_INFORMATION + '.' + DISALLOWED_TOTAL)
    ) {
      return this.handleTotalCostChange(name, value);
    } else {
      let newLineItem = _.cloneDeep(this.props.item);
      _.set(newLineItem, name, value);
      newLineItem = this.getItemWithChanges(this.props.originalItem, newLineItem);
      return this.handleChange(newLineItem);
    }
  };

  updateCategorizedSumArray = (lineItem) => {
    if (!lineItem) return;
    const scopeOfWorkTotal = _.get(
      lineItem,
      ADDITIONAL_INFORMATION + '.' + SCOPE_OF_WORK_TOTAL,
      [0, 0, 0]
    );
    const allowedExcessTotal = _.get(
      lineItem,
      ADDITIONAL_INFORMATION + '.' + ALLOWED_EXCESS_TOTAL,
      [0, 0, 0]
    );
    const disallowedTotal = _.get(
      lineItem,
      ADDITIONAL_INFORMATION + '.' + DISALLOWED_TOTAL,
      [0, 0, 0]
    );
    let categorizedSumArray = [];
    let actualAllowedTotals = [];
    _.forEach(scopeOfWorkTotal, (val, idx) => {
      categorizedSumArray.push(
        safeAdd(val, allowedExcessTotal[idx], disallowedTotal[idx])
      );
      actualAllowedTotals.push(safeAdd(val, allowedExcessTotal[idx]));
    });
    _.set(lineItem, ADDITIONAL_INFORMATION + '.' + CATEGORIZED_SUM, categorizedSumArray);
    _.set(
      lineItem,
      ADDITIONAL_INFORMATION + '.' + ACTUAL_ALLOWED_TOTAL,
      actualAllowedTotals
    );
  };

  handleTotalCostChange = (attributeName, value) => {
    let newLineItem = _.cloneDeep(this.state.lineItem);

    _.set(newLineItem, attributeName, value);
    this.updateCategorizedSumArray(newLineItem);
    const actualAllowedTotals = calculateItemCategorizedCostTotal(
      newLineItem,
      ACTUAL_ALLOWED_TOTAL,
      true
    );
    const disAllowedTotals = calculateItemCategorizedCostTotal(
      newLineItem,
      DISALLOWED_TOTAL,
      false
    );
    const allowedExcessTotals = calculateItemCategorizedCostTotal(
      newLineItem,
      ALLOWED_EXCESS_TOTAL,
      true
    );
    const fullyInScopeTotals = calculateItemCategorizedCostTotal(
      newLineItem,
      SCOPE_OF_WORK_TOTAL,
      true
    );
    const categorizedSumTotals = calculateItemCategorizedCostTotal(
      newLineItem,
      CATEGORIZED_SUM,
      true
    );
    _.set(
      newLineItem,
      ADDITIONAL_INFORMATION + '.' + ACTUAL_ALLOWED_TOTAL + 'Sum',
      actualAllowedTotals
    );
    _.set(
      newLineItem,
      ADDITIONAL_INFORMATION + '.' + DISALLOWED_TOTAL + 'Sum',
      disAllowedTotals
    );
    _.set(
      newLineItem,
      ADDITIONAL_INFORMATION + '.' + ALLOWED_EXCESS_TOTAL + 'Sum',
      allowedExcessTotals
    );
    _.set(
      newLineItem,
      ADDITIONAL_INFORMATION + '.' + SCOPE_OF_WORK_TOTAL + 'Sum',
      fullyInScopeTotals
    );
    _.set(
      newLineItem,
      ADDITIONAL_INFORMATION + '.' + CATEGORIZED_SUM + 'Sum',
      categorizedSumTotals
    );

    newLineItem = this.getItemWithChanges(this.props.originalItem, newLineItem);
    return this.handleChange(newLineItem);
  };

  handleTotalSumCostChange = (attributeName, value) => {
    let newLineItem = _.cloneDeep(this.state.lineItem);

    _.set(newLineItem, attributeName, value);

    const size = _.get(newLineItem, ADDITIONAL_INFORMATION + '.' + ACTUAL_TOTAL).length;

    const disAllowedTotalSum = _.get(
      newLineItem,
      ADDITIONAL_INFORMATION + '.' + DISALLOWED_TOTAL + 'Sum'
    );

    const allowedExcessTotalSum = _.get(
      newLineItem,
      ADDITIONAL_INFORMATION + '.' + ALLOWED_EXCESS_TOTAL + 'Sum'
    );

    const fullyInScopeTotalSum = _.get(
      newLineItem,
      ADDITIONAL_INFORMATION + '.' + SCOPE_OF_WORK_TOTAL + 'Sum'
    );

    const actualAllowedTotalSum = safeAdd(allowedExcessTotalSum, fullyInScopeTotalSum);
    _.set(
      newLineItem,
      ADDITIONAL_INFORMATION + '.' + ACTUAL_ALLOWED_TOTAL + 'Sum',
      actualAllowedTotalSum
    );
    _.set(
      newLineItem,
      ADDITIONAL_INFORMATION + '.' + ACTUAL_ALLOWED_TOTAL,
      this.splitTotal(actualAllowedTotalSum, size)
    );

    const categorizedSumSum = safeAdd(
      disAllowedTotalSum,
      allowedExcessTotalSum,
      fullyInScopeTotalSum
    );
    _.set(
      newLineItem,
      ADDITIONAL_INFORMATION + '.' + CATEGORIZED_SUM + 'Sum',
      categorizedSumSum
    );
    _.set(
      newLineItem,
      ADDITIONAL_INFORMATION + '.' + CATEGORIZED_SUM,
      this.splitTotal(categorizedSumSum, size)
    );

    _.set(
      newLineItem,
      ADDITIONAL_INFORMATION + '.' + DISALLOWED_TOTAL,
      this.splitTotal(disAllowedTotalSum, size)
    );
    _.set(
      newLineItem,
      ADDITIONAL_INFORMATION + '.' + ALLOWED_EXCESS_TOTAL,
      this.splitTotal(allowedExcessTotalSum, size)
    );
    _.set(
      newLineItem,
      ADDITIONAL_INFORMATION + '.' + SCOPE_OF_WORK_TOTAL,
      this.splitTotal(fullyInScopeTotalSum, size)
    );

    newLineItem = this.getItemWithChanges(this.props.originalItem, newLineItem);
    return this.handleChange(newLineItem);
  };

  handleNotFullyInScopeItemChange = (name, value, ind = -1) => {
    let oldItem = this.props.originalItem;
    let newItem = this.state.lineItem;

    let newLineItem = oldItem ? _.cloneDeep(oldItem) : _.cloneDeep(newItem);
    _.set(newLineItem, name, value);

    newLineItem = this.resetBudgetCategorization(newLineItem, value);
    newLineItem = this.getItemWithChanges(this.props.originalItem, newLineItem);
    return this.handleChange(newLineItem);
  };

  handleNotFullyInScopeItemChangeAndBlur = (name, value, fieldName) => {
    this.handleNotFullyInScopeItemChange(name, value).then((res) => {
      this.handleBlur(fieldName);
    });
  };

  handleItemChangeAndBlur = (name, value, fieldName) => {
    this.handleItemChange(name, value).then((res) => {
      this.handleBlur(fieldName);
    });
  };

  handleItemChangeAndEditMode = (name, value, fieldName) => {
    this.handleItemChangeAndBlur(name, value, fieldName);
  };

  getItemWithChanges = (oldItem, newItemIn) => {
    const {itemConfig} = this.props;
    const totalOld = calculateItemActualCostTotal(oldItem);
    const total = calculateItemCategorizedCostTotal(
      newItemIn,
      ACTUAL_ALLOWED_TOTAL,
      true
    );

    let newItem = produce(newItemIn, (draft) => {
      return diffItem(
        draft,
        oldItem,
        _.get(itemConfig, 'labels'),
        this.props.itemCodeOptions
      );
    });

    let changes = [];

    if (oldItem) {
      if (_.isNumber(total) && _.toNumber(total) !== _.toNumber(totalOld)) {
        changes.push(
          createChangeObj(
            CHANGES_TYPES.VALUE_CHANGE,
            ACTUAL_TOTAL,
            totalOld,
            total,
            VALUE_CHANGE_DISPLAY[ACTUAL_TOTAL]
          )
        );
      }
    }

    let updatedItem = produce(newItem, (draft) =>
      _.set(
        draft,
        [ADDITIONAL_INFORMATION, 'changes'],
        _.concat(_.get(newItem, 'additionalInformation.changes'), changes)
      )
    );

    return updatedItem;
  };

  renderChangesSection = () => {
    return <React.Fragment />;
  };

  render() {
    return (
      <SimpleShared
        lineItem={this.state.lineItem}
        editMode={this.state.editMode}
        toggleEditMode={this.toggleEditMode}
        handleItemChangeAndEditMode={this.handleItemChangeAndEditMode}
        renderChangesSection={this.renderChangesSection}
        handleChange={this.handleChange}
        handleBlur={this.handleBlur}
        handleItemChange={this.handleItemChange}
        handleItemChangeAndBlur={this.handleItemChangeAndBlur}
        handleNotFullyInScopeItemChange={this.handleNotFullyInScopeItemChange}
        handleNotFullyInScopeItemChangeAndBlur={
          this.handleNotFullyInScopeItemChangeAndBlur
        }
        {...this.props}
      />
    );
  }
}
